'use client'

import { BodyText, LabelText } from '@/components/ui/typography'
import { Link } from '@/i18n/routing'
import { IconArrowRight } from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'

type ContentCardProps = {
  label: string
  title: string
  subtitle?: string
  tagsText?: string
  actionHref: string
  actionLabel: string
  rightAction?: ReactNode
  detail?: ReactNode
  className?: string
}

export function ContentCard({
  label,
  title,
  subtitle,
  tagsText,
  actionHref,
  actionLabel,
  rightAction,
  detail,
  className
}: ContentCardProps) {
  return (
    <article
      className={cn(
        'group bg-card hover:border-foreground/20 hover:bg-muted/20 focus-within:ring-ring/35 relative flex min-w-0 flex-col overflow-hidden rounded-lg border p-5 transition focus-within:ring-2',
        className
      )}
    >
      <Link
        href={actionHref}
        aria-label={`${actionLabel}: ${title}`}
        className="absolute inset-0 z-10 rounded-lg focus-visible:outline-none"
      />
      <div className="pointer-events-none relative z-20 flex min-h-full min-w-0 flex-1 flex-col">
        <div className="flex min-w-0 items-start justify-between gap-2">
          <LabelText className="text-muted-foreground min-w-0 truncate text-xs tracking-widest uppercase">
            {label}
          </LabelText>
          {rightAction && (
            <div className="pointer-events-auto relative z-30 -mt-1 -mr-1">
              {rightAction}
            </div>
          )}
        </div>
        <h3 className="mt-2 truncate text-xl font-semibold">{title}</h3>
        {subtitle && (
          <BodyText className="text-muted-foreground text-sm">
            {subtitle}
          </BodyText>
        )}
        {tagsText && (
          <BodyText className="text-muted-foreground text-xs">
            {tagsText}
          </BodyText>
        )}
        {detail}
        <span className="text-foreground mt-auto inline-flex w-fit items-center gap-2 text-sm font-semibold">
          {actionLabel}
          <span className="bg-foreground text-background inline-flex h-7 w-7 items-center justify-center rounded-full transition-transform group-hover:translate-x-0.5">
            <IconArrowRight className="h-4 w-4" />
          </span>
        </span>
      </div>
    </article>
  )
}
